import React, { useEffect, useState } from 'react';
import {
  PaymentElement,
  AddressElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import * as Sentry from '@sentry/react';

import { getCheckoutErrorMessage } from '../../service/checkoutErrors';
import { RETURN_PATHS, checkoutReturnUrl } from '../../service/checkoutReturn';
import '../../styles/App.css';

// Scoped copy of components/stripe/CheckoutForm.js rather than a shared
// edit to it, so the invoice-payment flow this feature runs alongside
// can't regress. The one real difference: the enroll contract says
// monthly billing may come back as "a subscription-setup equivalent" —
// which could be a Stripe SetupIntent (client secret prefixed "seti_")
// rather than a PaymentIntent ("pi_"), so this form picks the right
// confirm call based on that prefix instead of assuming PaymentIntent.
// `submitLabel` and `summary` are optional and default to the member
// flow's existing behavior, so the member call site (Membership2026.js)
// is unaffected by the guest flow's needs — it passes neither.
export default function MembershipCheckoutForm({
  clientSecret,
  returnPath = RETURN_PATHS.membership,
  submitLabel = 'Confirm membership',
  summary = null,
}) {
  const stripe = useStripe();
  const elements = useElements();

  const [message, setMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Stripe throws IntegrationError if confirmPayment runs before the
  // PaymentElement has mounted and emitted `ready` — which is exactly what
  // happened in production (Sentry KBT-FRONTEND-2). `elements` being
  // non-null is NOT the same as the Element being ready, so the old
  // `!stripe || !elements` guard let a fast click through.
  const [elementReady, setElementReady] = useState(false);

  const isSetupIntent = typeof clientSecret === 'string' && clientSecret.startsWith('seti_');

  useEffect(() => {
    if (!stripe) return;

    const paramName = isSetupIntent ? 'setup_intent_client_secret' : 'payment_intent_client_secret';
    const returnedSecret = new URLSearchParams(window.location.search).get(paramName);
    if (!returnedSecret) return;

    const check = isSetupIntent
      ? stripe.retrieveSetupIntent(returnedSecret).then(({ setupIntent }) => setupIntent?.status)
      : stripe.retrievePaymentIntent(returnedSecret).then(({ paymentIntent }) => paymentIntent?.status);

    check.then((status) => {
      switch (status) {
        case 'succeeded':
          setMessage('Membership enrollment succeeded!');
          break;
        case 'processing':
          setMessage('Your enrollment is processing.');
          break;
        case 'requires_payment_method':
          setMessage('Your payment was not successful, please try again.');
          break;
        default:
          setMessage('Something went wrong.');
          break;
      }
    });
  }, [stripe, isSetupIntent]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements || !elementReady || isLoading) return;

    setIsLoading(true);
    setMessage(null);

    const confirmParams = { return_url: checkoutReturnUrl(returnPath) };

    // try/finally is the important part: Stripe THROWS for integration
    // faults instead of returning { error }, and without this the throw
    // escaped, setIsLoading(false) never ran, and the button stayed stuck
    // spinning — leaving the payer unable to retry at all. Losing someone
    // at the card form is the most expensive place to lose them.
    try {
      const { error } = isSetupIntent
        ? await stripe.confirmSetup({ elements, confirmParams })
        : await stripe.confirmPayment({ elements, confirmParams });

      if (error) setMessage(getCheckoutErrorMessage(error));
    } catch (err) {
      // Reported explicitly: a thrown Stripe fault is our integration bug,
      // and we want it in Sentry with this component as context rather than
      // only as an anonymous window.onerror.
      Sentry.captureException(err, { tags: { area: 'membership-checkout' } });
      setMessage(getCheckoutErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form id="membership-payment-form" className="stripe-elements-form" onSubmit={handleSubmit}>
      {summary}
      <AddressElement options={{ mode: 'billing' }} />
      <PaymentElement
        id="membership-payment-element"
        options={{ layout: 'tabs' }}
        onReady={() => setElementReady(true)}
      />
      <button disabled={isLoading || !stripe || !elements || !elementReady} id="submit">
        <span id="button-text">
          {isLoading ? <div className="spinner" id="spinner"></div> : submitLabel}
        </span>
      </button>
      {message && <div id="payment-message">{message}</div>}
    </form>
  );
}
